import { Button, Heading } from '@dittolive/anvil'
import { Container } from 'components/layout'
import { Link } from 'components/link'
import { useCallback } from 'react'
import { FallbackProps } from 'react-error-boundary'
import { hardRefresh } from 'utils'

import {
  authErrorHeader,
  CODE_TO_HEADER,
  CODE_TO_MESSAGE,
  isAuthError,
  PortalError,
} from './error'
import ErrorHeaderLogo from './ErrorHeaderLogo'

/**
 * A fallback UI to display when an error occurs in the app.
 *
 * If the error is a PortalError, it will display relevant information based on
 * the error code. Otherwise, the messaging will be pulled from the error, itself,
 * if available.
 */
export default function ErrorFallback({ error }: FallbackProps) {
  return <ErrorComponent error={error} />
}

type Props = {
  /** An optional prop to override the default "Try again" action */
  primaryAction?: { label: string; onClick: () => void }
} & Pick<FallbackProps, 'error'>

/** A more flexible version of the ErrorFallback that can be used as a standalone component. */
export function ErrorComponent({ error, primaryAction }: Props) {
  const handleGoHome = async () => {
    await hardRefresh('/')
  }

  const handlePrimaryButton = useCallback(async () => {
    // Call the provided primary action, if defined
    if (primaryAction) {
      primaryAction.onClick()
      return
    }

    // Otherwise handle the "Try again" button click with a hard refresh
    await hardRefresh()
  }, [primaryAction])

  /**
   * The header to display based on the error code, if available.
   *
   * Note that RelayNetworkLayer does not support throwing custom errors when it aborts a fetch
   * after our retry limit is reached, so we check the message string for this scenario
   */
  const renderHeader = () => {
    // Handle errors from Auth0
    if (isAuthError(error)) {
      return authErrorHeader(error)
    }

    if (error instanceof PortalError) {
      return CODE_TO_HEADER[error.code] || FALLBACK_HEADER
    }

    const message = error?.message
    if (message === CODE_TO_MESSAGE['RELAY_FETCH_FAILED']) {
      return CODE_TO_HEADER['RELAY_FETCH_FAILED'] || FALLBACK_HEADER
    }

    return FALLBACK_HEADER
  }

  /**
   * The message to display based on the error code, if available
   */
  const renderMessage = () => {
    if (error instanceof PortalError) {
      return CODE_TO_MESSAGE[error.code] || FALLBACK_MESSAGE
    } else {
      return error?.message || FALLBACK_MESSAGE
    }
  }

  return (
    <Container className="flex h-screen flex-col items-center justify-center space-y-8">
      <ErrorHeaderLogo />

      <div className="text-center md:max-w-md">
        <Heading level={1} className="mb-4 text-2xl" data-testid="errorHeader">
          {renderHeader()}
        </Heading>
        <p
          className="text-foreground-subtle text-base leading-5"
          data-testid="errorMessage"
        >
          {renderMessage()}
        </p>
      </div>

      <div
        className="flex h-10 justify-center space-x-2"
        data-testid="errorActions"
      >
        <Button asChild data-testid="errorHomeButton">
          <Link to="/" onClick={handleGoHome}>
            Go home
          </Link>
        </Button>
        <Button
          variant="primary"
          onClick={handlePrimaryButton}
          data-testid="errorPrimaryButton"
        >
          {primaryAction?.label || 'Try again'}
        </Button>
      </div>
    </Container>
  )
}

const FALLBACK_HEADER = 'Something went wrong'
const FALLBACK_MESSAGE = 'An unexpected error occurred. Please try again later.'
